import { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api, notifyError } from "@/lib/api";
import { Plus, X, Trash, FileArrowDown, Coins, CheckCircle, Circle, CheckSquare, Square } from "@phosphor-icons/react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart, Line, Legend } from "recharts";

const CATS = ["Equipment", "Marketing", "Training", "Travel", "Personnel", "Consumables", "Overheads"];
const COLORS = ["#FFB020", "#00A86B", "#E63946", "#4ECDC4", "#C77DFF", "#F72585", "#3A86FF"];
const CUR = { EUR: "€", GBP: "£", USD: "$" };
const sym = (c) => CUR[c] || "€";

function Ring({ done, total, size = 40, overdue = 0 }) {
  const pct = total ? done / total : 0;
  const r = (size - 6) / 2;
  const c = 2 * Math.PI * r;
  const color = overdue > 0 ? "#E63946" : (total > 0 && done === total ? "#00A86B" : "#FFB020");
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} data-testid="milestone-ring">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="4" />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth="4" strokeLinecap="round"
        strokeDasharray={c} strokeDashoffset={c * (1 - pct)} transform={`rotate(-90 ${size / 2} ${size / 2})`} />
      <text x="50%" y="53%" textAnchor="middle" dominantBaseline="middle" fill="#fff" fontSize="10" fontFamily="monospace">{done}/{total}</text>
    </svg>
  );
}

export default function Grants() {
  const [grants, setGrants] = useState([]);
  const [open, setOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const emptyForm = { name: "", programme: "Enterprise Ireland", programme_id: "ei", agency: "Enterprise Ireland", currency: "EUR", total_awarded: "", start_date: "", end_date: "", notes: "" };
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => { const r = await api.get("/grants"); setGrants(r.data); }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/grants/catalog").then((r) => setCatalog(r.data.programmes || [])).catch(() => {}); }, []);

  const selectedProg = catalog.find((p) => p.id === form.programme_id);
  const applyProgramme = (id) => {
    const p = catalog.find((x) => x.id === id);
    setForm((f) => ({ ...f, programme_id: id, programme: p && p.id !== "other" ? p.name : "", agency: p?.agency || "", currency: p?.currency || f.currency }));
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/grants", {
        name: form.name, programme: form.programme || (selectedProg?.name ?? "Other"), agency: form.agency,
        currency: form.currency, total_awarded: Number(form.total_awarded),
        start_date: form.start_date, end_date: form.end_date, notes: form.notes,
      });
      setOpen(false);
      setForm(emptyForm);
      load();
    } catch (e) { notifyError(e); }
  };

  const del = async (id) => {
    if (!confirm("Delete this grant and all its expenses?")) return;
    await api.delete(`/grants/${id}`); load();
  };

  const detail = grants.find(g => g.grant_id === detailId);

  const [progFilter, setProgFilter] = useState("all");
  const [curFilter, setCurFilter] = useState("all");
  const activeGrants = grants.filter(g => g.active);
  const progOptions = [...new Set(activeGrants.map(g => g.programme).filter(Boolean))];
  const curOptions = [...new Set(activeGrants.map(g => g.currency || "EUR"))];
  const filteredActive = activeGrants.filter(g =>
    (progFilter === "all" || g.programme === progFilter) &&
    (curFilter === "all" || (g.currency || "EUR") === curFilter));
  const totalCashEur = filteredActive.reduce((s, g) => s + (g.cash_position_eur || 0), 0);
  const totalReceivedEur = filteredActive.reduce((s, g) => s + (g.received_eur || 0), 0);
  const totalSpentEur = filteredActive.reduce((s, g) => s + (g.spent_eur || 0), 0);
  const mixedCurrency = new Set(filteredActive.map(g => g.currency || "EUR")).size > 1;

  const downloadSummary = () => {
    const scope = `${progFilter === "all" ? "All programmes" : progFilter} · ${curFilter === "all" ? "All currencies" : curFilter}`;
    const rows = [
      ["PerCen360 — Grant Cash-on-Hand Summary"],
      ["Generated", new Date().toISOString()],
      ["Filter", scope],
      ["Active grants in view", filteredActive.length],
      [""],
      ["Totals (EUR)", "Received", "Spent", "Cash on hand"],
      ["", totalReceivedEur.toFixed(2), totalSpentEur.toFixed(2), totalCashEur.toFixed(2)],
      [""],
      ["Grant", "Programme", "Currency", "Received (native)", "Spent (native)", "Cash (native)", "Received (EUR)", "Spent (EUR)", "Cash (EUR)"],
    ];
    filteredActive.forEach(g => rows.push([
      g.name, g.programme || "", g.currency || "EUR",
      Number(g.received || 0).toFixed(2), Number(g.spent || 0).toFixed(2), Number(g.cash_position || 0).toFixed(2),
      Number(g.received_eur || 0).toFixed(2), Number(g.spent_eur || 0).toFixed(2), Number(g.cash_position_eur || 0).toFixed(2),
    ]));
    const csv = rows.map(r => r.map(c => `"${(c ?? "").toString().replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `grant_cash_summary_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow mb-2">Grant Guardian</div>
          <h1 className="font-display text-4xl">Track & report grants</h1>
          <p className="text-white/60 text-sm mt-2">Enterprise Ireland · Innovate UK · US SBIR/STTR · Horizon Europe. Every award guarded with an audit hash.</p>
        </div>
        <button data-testid="btn-new-grant" onClick={() => setOpen(true)} className="btn-gold"><Plus weight="bold" size={16}/> New grant</button>
      </div>

      {activeGrants.length > 0 && (
        <motion.div initial={{opacity:0,y:10}} animate={{opacity:1,y:0}}
          className="glass rounded-2xl p-6 md:p-7" data-testid="grants-cash-summary">
          <div className="flex flex-wrap items-center justify-between gap-6">
            <div>
              <div className="flex items-center gap-2 eyebrow mb-2"><Coins size={14} weight="fill" /> Cash on hand · active grants</div>
              <div className="mono text-4xl md:text-5xl" style={{ color: totalCashEur < 0 ? "#E63946" : "#00A86B" }} data-testid="total-cash-on-hand">
                €{Number(totalCashEur).toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </div>
              <div className="text-xs text-white/45 mt-2">
                Across {filteredActive.length} {progFilter === "all" && curFilter === "all" ? "active" : "matching"} grant{filteredActive.length !== 1 ? "s" : ""}{mixedCurrency ? " · normalised to EUR at today's ECB rate" : ""}. Money received minus money spent.
              </div>
            </div>
            <div className="flex items-center gap-8">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">Received</div>
                <div className="mono text-2xl mt-1" data-testid="total-received">€{Number(totalReceivedEur).toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">Spent</div>
                <div className="mono text-2xl mt-1 text-[#FFB020]" data-testid="total-spent">€{Number(totalSpentEur).toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
              </div>
              <button onClick={downloadSummary} className="btn-ghost text-xs whitespace-nowrap" data-testid="btn-export-summary"><FileArrowDown size={14} weight="fill"/> Export CSV</button>
            </div>
          </div>
          {(progOptions.length > 1 || curOptions.length > 1) && (
            <div className="flex flex-wrap items-center gap-3 mt-5 pt-4 border-t border-white/8">
              <span className="text-[10px] uppercase tracking-[0.2em] text-white/40">Filter total</span>
              {progOptions.length > 1 && (
                <select value={progFilter} onChange={(e) => setProgFilter(e.target.value)} data-testid="summary-filter-programme" className="text-xs !py-1.5 !w-auto">
                  <option value="all">All programmes</option>
                  {progOptions.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              )}
              {curOptions.length > 1 && (
                <select value={curFilter} onChange={(e) => setCurFilter(e.target.value)} data-testid="summary-filter-currency" className="text-xs !py-1.5 !w-auto">
                  <option value="all">All currencies</option>
                  {curOptions.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              )}
              {(progFilter !== "all" || curFilter !== "all") && (
                <button onClick={() => { setProgFilter("all"); setCurFilter("all"); }} className="btn-ghost text-[11px] py-1" data-testid="summary-filter-clear"><X size={12}/> Clear</button>
              )}
            </div>
          )}
        </motion.div>
      )}

      {grants.length === 0 ? (
        <div className="glass rounded-xl p-12 text-center text-white/50 text-sm">
          No grants tracked. Create one to begin guardianing.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {grants.map(g => {
            const pct = g.total_awarded > 0 ? (g.spent / g.total_awarded) * 100 : 0;
            let pctBadge = "badge-pass";
            if (pct > 90) pctBadge = "badge-fail";
            else if (pct > 70) pctBadge = "badge-warn";
            const barColor = pct > 90 ? "#E63946" : "linear-gradient(90deg,#00A86B,#FFB020)";
            return (
              <motion.div key={g.grant_id} initial={{opacity:0,y:12}} animate={{opacity:1,y:0}}
                className="glass rounded-xl p-6 hover:-translate-y-1 transition-all duration-300 cursor-pointer"
                onClick={() => setDetailId(g.grant_id)} data-testid={`card-grant-${g.grant_id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">{g.programme}</div>
                    <div className="font-display text-lg mt-1">{g.name}</div>
                  </div>
                  <button onClick={(e) => {e.stopPropagation(); del(g.grant_id);}} className="text-white/40 hover:text-[#E63946]" data-testid={`btn-del-grant-${g.grant_id}`}><Trash size={14}/></button>
                </div>
                <div className="mono text-3xl mt-4">{sym(g.currency)}{Number(g.remaining).toLocaleString()}<span className="text-white/40 text-sm ml-1">/ {sym(g.currency)}{Number(g.total_awarded).toLocaleString()}</span></div>
                <div className="h-1.5 rounded-full bg-white/5 overflow-hidden mt-4">
                  <div className="h-full rounded-full" style={{ width: `${Math.min(100,pct)}%`, background: barColor }} />
                </div>
                <div className="flex items-center justify-between mt-3 text-xs text-white/50">
                  <span>{g.expense_count || 0} expenses logged</span>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider ${pctBadge}`}>{pct.toFixed(0)}% used</span>
                </div>
                {(g.milestone_total > 0 || g.drawdown_count > 0) && (
                  <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/5" data-testid={`grant-footer-${g.grant_id}`}>
                    {g.milestone_total > 0 ? (
                      <div className="flex items-center gap-2">
                        <Ring done={g.milestone_done} total={g.milestone_total} overdue={g.milestone_overdue} />
                        <div className="text-[11px] text-white/50 leading-tight">Milestones<br/>{g.milestone_overdue > 0 ? <span className="text-[#E63946]">{g.milestone_overdue} overdue</span> : `${g.milestone_done}/${g.milestone_total} done`}</div>
                      </div>
                    ) : <span />}
                    {g.drawdown_count > 0 && (
                      <div className="text-right">
                        <div className="text-[10px] uppercase tracking-wider text-white/40">Cash on hand</div>
                        <div className="mono text-sm" style={{ color: g.cash_position < 0 ? "#E63946" : "#00A86B" }}>{sym(g.currency)}{Number(g.cash_position).toLocaleString()}</div>
                      </div>
                    )}
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      )}

      {/* New grant modal */}
      <AnimatePresence>
        {open && (
          <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto">
            <motion.div initial={{y:20,opacity:0}} animate={{y:0,opacity:1}} exit={{y:20,opacity:0}}
              className="glass rounded-2xl w-full max-w-xl my-8 p-8">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="eyebrow mb-1">New grant</div>
                  <div className="font-display text-2xl">Register award</div>
                </div>
                <button onClick={() => setOpen(false)} className="text-white/60 hover:text-white" data-testid="btn-close-grant-modal"><X size={20}/></button>
              </div>
              <form onSubmit={submit} className="space-y-4">
                <div><label>Grant name</label><input required data-testid="input-grant-name" value={form.name} onChange={e => setForm({...form, name: e.target.value})}/></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><label>Programme</label>
                    <select data-testid="select-programme" value={form.programme_id} onChange={e => applyProgramme(e.target.value)}>
                      {catalog.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>
                  <div><label>Currency</label>
                    <select data-testid="select-grant-currency" value={form.currency} onChange={e => setForm({...form, currency: e.target.value})}>
                      {["EUR","GBP","USD"].map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  {form.programme_id === "other" && (
                    <div className="col-span-2"><label>Programme name</label><input data-testid="input-programme-name" value={form.programme} onChange={e => setForm({...form, programme: e.target.value})} placeholder="e.g. SFI Frontiers"/></div>
                  )}
                  {selectedProg?.phases?.length > 0 && (
                    <div className="col-span-2"><label>Phase / typical award (prefills amount)</label>
                      <select data-testid="select-phase" defaultValue="" onChange={e => { const ph = selectedProg.phases[Number(e.target.value)]; if (ph) setForm(f => ({...f, total_awarded: String(ph.amount)})); }}>
                        <option value="">Choose a phase…</option>
                        {selectedProg.phases.map((ph, i) => <option key={i} value={i}>{ph.label} · {sym(selectedProg.currency)}{ph.amount.toLocaleString()}</option>)}
                      </select>
                    </div>
                  )}
                  <div><label>Total awarded ({sym(form.currency)})</label><input required type="number" step="0.01" data-testid="input-grant-total" value={form.total_awarded} onChange={e => setForm({...form, total_awarded: e.target.value})}/></div>
                  <div><label>Agency (optional)</label><input data-testid="input-grant-agency" value={form.agency} onChange={e => setForm({...form, agency: e.target.value})}/></div>
                  <div><label>Start date</label><input type="date" data-testid="input-grant-start" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})}/></div>
                  <div><label>End date</label><input type="date" data-testid="input-grant-end" value={form.end_date} onChange={e => setForm({...form, end_date: e.target.value})}/></div>
                </div>
                {selectedProg?.note && <div className="text-[11px] text-white/45">{selectedProg.note}</div>}
                <div><label>Notes</label><textarea rows={2} value={form.notes} onChange={e => setForm({...form, notes: e.target.value})}/></div>
                <div className="flex justify-end gap-3 pt-3 border-t border-white/5">
                  <button type="button" onClick={() => setOpen(false)} className="btn-ghost">Cancel</button>
                  <button type="submit" data-testid="btn-save-grant" className="btn-gold">Register grant</button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Detail modal */}
      <AnimatePresence>
        {detail && <GrantDetail grant={detail} onClose={() => { setDetailId(null); load(); }} />}
      </AnimatePresence>
    </div>
  );
}

const PACING = {
  over:     { label: "Over-pacing", color: "#E63946" },
  under:    { label: "Under-pacing", color: "#4ECDC4" },
  on_track: { label: "On track", color: "#00A86B" },
};

function GrantDetail({ grant, onClose }) {
  const [expenses, setExpenses] = useState([]);
  const [report, setReport] = useState(null);
  const [receipts, setReceipts] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [msForm, setMsForm] = useState({ title: "", due_date: "" });
  const [ddForm, setDdForm] = useState({ amount: "", date: new Date().toISOString().slice(0,10), currency: grant.currency || "EUR", note: "" });
  const [form, setForm] = useState({ category: "Equipment", amount: "", currency: "EUR", date: new Date().toISOString().slice(0,10), description: "", reference: "", receipt_id: "" });
  const [budgetOpen, setBudgetOpen] = useState(false);

  const load = useCallback(async () => {
    const [ex, rep, rc, ms] = await Promise.all([
      api.get(`/grants/${grant.grant_id}/expenses`),
      api.get(`/grants/${grant.grant_id}/report`),
      api.get(`/receipts`).catch(() => ({ data: [] })),
      api.get(`/grants/${grant.grant_id}/milestones`).catch(() => ({ data: [] })),
    ]);
    setExpenses(ex.data); setReport(rep.data); setReceipts(rc.data || []); setMilestones(ms.data || []);
  }, [grant.grant_id]);
  useEffect(() => { load(); }, [load]);

  const addMs = async (e) => { e.preventDefault(); if (!msForm.title.trim()) return; await api.post(`/grants/${grant.grant_id}/milestones`, msForm); setMsForm({ title: "", due_date: "" }); load(); };
  const toggleMs = async (m) => { await api.put(`/grants/${grant.grant_id}/milestones/${m.milestone_id}`, { done: !m.done }); load(); };
  const setMsChecklist = async (m, checklist) => { await api.put(`/grants/${grant.grant_id}/milestones/${m.milestone_id}`, { checklist }); load(); };
  const delMs = async (id) => { await api.delete(`/grants/${grant.grant_id}/milestones/${id}`); load(); };
  const addDd = async (e) => { e.preventDefault(); if (!ddForm.amount) return; await api.post(`/grants/${grant.grant_id}/drawdowns`, { ...ddForm, amount: Number(ddForm.amount) }); setDdForm({ amount: "", date: new Date().toISOString().slice(0,10), currency: grant.currency || "EUR", note: "" }); load(); };
  const delDd = async (id) => { await api.delete(`/grants/${grant.grant_id}/drawdowns/${id}`); load(); };

  const linkReceipt = (id) => {
    const r = receipts.find((x) => x.receipt_id === id);
    if (!r) { setForm((f) => ({ ...f, receipt_id: "", reference: "" })); return; }
    setForm((f) => ({
      ...f,
      receipt_id: id,
      reference: r.vendor || "",
      amount: String(r.amount ?? f.amount),
      date: (r.date || f.date).slice(0, 10),
      description: f.description || r.vendor || "",
      category: r.category && CATS.includes(r.category) ? r.category : f.category,
    }));
  };

  const addExp = async (e) => {
    e.preventDefault();
    await api.post(`/grants/${grant.grant_id}/expense`, { ...form, amount: Number(form.amount) });
    setForm({ category: "Equipment", amount: "", currency: "EUR", date: new Date().toISOString().slice(0,10), description: "", reference: "", receipt_id: "" });
    load();
  };

  const kpis = report?.kpis || {};
  const pacing = kpis.pacing ? PACING[kpis.pacing] : null;
  const cs = sym(grant.currency);

  const download = () => {
    if (!report) return;
    const rows = [["Grant Report", grant.name], ["Programme", grant.programme], ["Agency", grant.agency || ""], ["Currency", grant.currency || "EUR"], ["Total Awarded", `${cs}${grant.total_awarded}`],
                  ["Total Spent", `${cs}${report.total_spent}`], ["Remaining", `${cs}${report.remaining}`], ["Utilisation %", report.utilization_pct],
                  ["Audit Hash", report.audit_hash], ["Generated", report.generated_at], [""], ["Category Breakdown"], ["Category", "Amount"]];
    report.by_category.forEach(c => rows.push([c.category, c.amount]));
    rows.push([""], ["Budget vs Actual"], ["Category", "Allocated", "Spent", "Remaining", "% Used", "Over budget"]);
    (report.budget_lines || []).forEach(l => rows.push([l.category, l.allocated, l.spent, l.remaining, l.pct ?? "", l.over_budget ? "YES" : "No"]));
    rows.push([""], ["Expense Ledger"], ["Date", "Category", "Description", "Amount", "Receipt/Invoice ref"]);
    expenses.forEach(e => rows.push([e.date, e.category, e.description || "", e.amount, e.reference || (e.receipt_id ? "linked" : "")]));
    const csv = rows.map(r => r.map(c => `"${(c ?? "").toString().replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `grant_report_${grant.name.replace(/\s+/g, "_")}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto">
      <motion.div initial={{y:20,opacity:0}} animate={{y:0,opacity:1}} exit={{y:20,opacity:0}}
        className="glass rounded-2xl w-full max-w-4xl my-8 p-8">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">{grant.programme}</div>
            <div className="font-display text-3xl mt-1">{grant.name}</div>
            {report && <div className="text-xs text-white/40 mt-2 mono">Audit hash: {report.audit_hash}</div>}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={download} data-testid="btn-download-report" className="btn-gold text-sm"><FileArrowDown size={16} weight="fill"/> Compliance CSV</button>
            <button onClick={onClose} className="text-white/60 hover:text-white" data-testid="btn-close-detail"><X size={20}/></button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="grant-kpis">
          <KpiTile label="Utilisation" value={`${report?.utilization_pct || 0}%`} hint={`${cs}${Number(report?.remaining||0).toLocaleString()} left`} />
          <KpiTile label="Burn / month" value={`${cs}${Number(kpis.burn_rate_monthly||0).toLocaleString()}`} hint={kpis.time_pct != null ? `${kpis.time_pct}% time elapsed` : "—"} />
          <KpiTile label="Pacing" value={pacing ? pacing.label : "—"} color={pacing?.color} hint={kpis.days_remaining != null ? `${kpis.days_remaining}d remaining` : "no dates set"} />
          <KpiTile label="Documented" value={`${kpis.documented_count || 0}/${kpis.expense_count || 0}`} hint={kpis.undocumented_count ? `${kpis.undocumented_count} missing ref` : "all traceable"} warn={kpis.undocumented_count > 0} />
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div className="rounded-xl bg-black/30 border border-white/5 p-5">
            <div className="eyebrow mb-2">Utilisation</div>
            <div className="mono text-3xl">{cs}{Number(report?.total_spent||0).toLocaleString()} <span className="text-white/40 text-base">of {cs}{Number(grant.total_awarded).toLocaleString()}</span></div>
            <div className="text-white/60 text-sm mt-1">{report?.utilization_pct || 0}% used · {cs}{Number(report?.remaining||0).toLocaleString()} remaining</div>
            {report?.allocated_total != null && (
              <div className="text-xs text-white/45 mt-3">Budgeted {cs}{Number(report.allocated_total).toLocaleString()} · Unallocated {cs}{Number(report.unallocated).toLocaleString()}</div>
            )}
          </div>
          <div className="rounded-xl bg-black/30 border border-white/5 p-5 h-56">
            <div className="eyebrow mb-2">By Category</div>
            {report?.by_category?.length > 0 ? (
              <ResponsiveContainer>
                <BarChart data={report.by_category}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="category" tick={{ fill: "#9BA3A0", fontSize: 10 }} />
                  <YAxis tick={{ fill: "#9BA3A0", fontSize: 10 }} />
                  <Tooltip contentStyle={{ background:"#0A1311", border:"1px solid rgba(255,255,255,0.1)", borderRadius:10 }}/>
                  <Bar dataKey="amount" fill="#FFB020" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <div className="text-white/50 text-xs pt-6 text-center">No expenses yet.</div>}
          </div>
        </div>

        {report?.fx_snapshot && report.fx_snapshot.currency !== "EUR" && (
          <div className="rounded-xl bg-black/30 border border-white/5 p-5 mb-6" data-testid="fx-snapshot">
            <div className="flex items-center justify-between mb-3">
              <div className="eyebrow">FX snapshot</div>
              <div className="text-[11px] text-white/40">1 {report.fx_snapshot.currency} ≈ €{Number(report.fx_snapshot.eur_rate || 0).toFixed(4)} · as of {report.fx_snapshot.as_of}</div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[["Award", "award", "award_eur"], ["Received", "received", "received_eur"], ["Spent", "spent", "spent_eur"], ["Remaining", "remaining", "remaining_eur"]].map(([lbl, nk, ek]) => (
                <div key={nk} className="rounded-lg bg-black/20 p-3">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">{lbl}</div>
                  <div className="mono text-lg mt-1">{cs}{Number(report.fx_snapshot[nk]).toLocaleString()}</div>
                  <div className="text-[11px] text-white/45">≈ €{Number(report.fx_snapshot[ek]).toLocaleString()}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Milestones & reporting dates */}
        <div className="rounded-xl bg-black/20 border border-white/5 p-5 mb-6" data-testid="milestones-section">
          <div className="flex items-center justify-between mb-3">
            <div className="eyebrow">Milestones &amp; reporting dates</div>
            {milestones.length > 0 && <Ring done={milestones.filter(m => m.done).length} total={milestones.length} overdue={milestones.filter(m => !m.done && m.due_date && m.due_date < new Date().toISOString().slice(0,10)).length} />}
          </div>
          <form onSubmit={addMs} className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end mb-4">
            <input className="md:col-span-3" placeholder="e.g. Phase I final report" value={msForm.title} onChange={e => setMsForm({ ...msForm, title: e.target.value })} data-testid="input-milestone-title" />
            <input type="date" className="md:col-span-2" value={msForm.due_date} onChange={e => setMsForm({ ...msForm, due_date: e.target.value })} data-testid="input-milestone-date" />
            <button type="submit" className="btn-gold text-sm" data-testid="btn-add-milestone"><Plus weight="bold" size={14}/> Add</button>
          </form>
          {milestones.length === 0 ? (
            <div className="text-white/40 text-xs">No milestones yet. Add phase reviews, drawdowns or reporting dates — you&apos;ll be reminded ahead of each (if deadline reminders are on).</div>
          ) : (
            <div className="space-y-2">
              {milestones.map((m) => {
                const cl = m.checklist || [];
                const clDone = cl.filter(c => c.done).length;
                return (
                <div key={m.milestone_id} className="rounded-lg bg-black/25 border border-white/5 p-3" data-testid={`milestone-${m.milestone_id}`}>
                  <div className="flex items-center gap-3">
                    <button onClick={() => toggleMs(m)} data-testid={`milestone-toggle-${m.milestone_id}`}>
                      {m.done ? <CheckCircle size={20} weight="fill" color="#00A86B" /> : <Circle size={20} color="#8A9A94" />}
                    </button>
                    <div className="flex-1">
                      <div className={`text-sm ${m.done ? "text-white/40 line-through" : "text-white/85"}`}>{m.title}</div>
                      {m.due_date && <div className="text-[11px] text-white/45 mono">Due {m.due_date}</div>}
                    </div>
                    {cl.length > 0 && (
                      <span className="text-[10px] mono px-2 py-0.5 rounded-full" data-testid={`checklist-count-${m.milestone_id}`}
                        style={{ background: clDone === cl.length ? "#00A86B22" : "#FFB02022", color: clDone === cl.length ? "#00A86B" : "#FFB020" }}>
                        {clDone}/{cl.length} evidence
                      </span>
                    )}
                    <button onClick={() => delMs(m.milestone_id)} className="text-white/40 hover:text-[#E63946]" data-testid={`milestone-del-${m.milestone_id}`}><Trash size={14}/></button>
                  </div>
                  <MilestoneChecklist m={m} onChange={(list) => setMsChecklist(m, list)} />
                </div>
                );
              })}
            </div>
          )}
          <datalist id="evidence-suggestions">
            {["Financial report", "Progress / technical report", "Receipts & invoices", "Timesheets", "Bank statement", "Signed deliverable", "Milestone sign-off"].map(s => <option key={s} value={s}/>)}
          </datalist>
        </div>

        {report?.series?.length > 0 && (
          <div className="rounded-xl bg-black/20 border border-white/5 p-5 mb-6" data-testid="cashflow-chart">
            <div className="eyebrow mb-3">Received vs spent over time</div>
            <div style={{ width: "100%", height: 220 }}>
              <ResponsiveContainer>
                <LineChart data={report.series} margin={{ top: 5, right: 12, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="month" stroke="#8A9A94" fontSize={11} />
                  <YAxis stroke="#8A9A94" fontSize={11} tickFormatter={(v) => `${cs}${(v / 1000).toFixed(0)}k`} />
                  <Tooltip contentStyle={{ background: "#0B1512", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 }}
                    formatter={(v, n) => [`${cs}${Number(v).toLocaleString()}`, n === "received" ? "Received" : "Spent"]} />
                  <Legend formatter={(v) => v === "received" ? "Received" : "Spent"} />
                  <Line type="monotone" dataKey="received" stroke="#00A86B" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="spent" stroke="#FFB020" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="text-[11px] text-white/40 mt-2">Cumulative money received vs spent — a widening gap where spend outpaces receipts signals a cash squeeze.</div>
          </div>
        )}

        {/* Drawdowns — money received */}
        <div className="rounded-xl bg-black/20 border border-white/5 p-5 mb-6" data-testid="drawdowns-section">
          <div className="flex items-center justify-between mb-3">
            <div className="eyebrow">Drawdowns — money received</div>
            <div className="text-right">
              <div className="text-[11px] text-white/45">Received {cs}{Number(report?.total_received || 0).toLocaleString()}</div>
              <div className="text-sm mono" style={{ color: (report?.cash_position || 0) < 0 ? "#E63946" : "#00A86B" }} data-testid="cash-position">Cash on hand {cs}{Number(report?.cash_position || 0).toLocaleString()}</div>
            </div>
          </div>
          <form onSubmit={addDd} className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end mb-4">
            <input required type="number" step="0.01" placeholder="Amount received" className="md:col-span-2" value={ddForm.amount} onChange={e => setDdForm({ ...ddForm, amount: e.target.value })} data-testid="input-drawdown-amount" />
            <select value={ddForm.currency} onChange={e => setDdForm({ ...ddForm, currency: e.target.value })} data-testid="select-drawdown-currency">
              {["EUR","GBP","USD"].map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <input type="date" value={ddForm.date} onChange={e => setDdForm({ ...ddForm, date: e.target.value })} data-testid="input-drawdown-date" />
            <input placeholder="Note" value={ddForm.note} onChange={e => setDdForm({ ...ddForm, note: e.target.value })} data-testid="input-drawdown-note" />
            <button type="submit" className="btn-gold text-sm" data-testid="btn-add-drawdown"><Plus weight="bold" size={14}/> Record</button>
          </form>
          {(report?.drawdowns || []).length === 0 ? (
            <div className="text-white/40 text-xs">No drawdowns recorded. Log money actually received to see your true cash position vs spend.</div>
          ) : (
            <div className="space-y-1.5">
              {report.drawdowns.map((d) => (
                <div key={d.drawdown_id} className="flex items-center gap-3 rounded-lg bg-black/25 border border-white/5 p-2.5" data-testid={`drawdown-${d.drawdown_id}`}>
                  <div className="mono text-sm">{cs}{Number(d.amount).toLocaleString()}{d.currency && d.currency !== grant.currency ? <span className="text-[10px] text-white/40 ml-1">({Number(d.original_amount).toLocaleString()} {d.currency})</span> : null}</div>
                  <div className="flex-1 text-[11px] text-white/45 truncate">{d.date}{d.note ? ` · ${d.note}` : ""}</div>
                  <button onClick={() => delDd(d.drawdown_id)} className="text-white/40 hover:text-[#E63946]" data-testid={`drawdown-del-${d.drawdown_id}`}><Trash size={14}/></button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Budget vs actual */}
        <div className="rounded-xl bg-black/20 border border-white/5 p-5 mb-6" data-testid="budget-vs-actual">
          <div className="flex items-center justify-between mb-4">
            <div className="eyebrow">Budget vs actual</div>
            <button onClick={() => setBudgetOpen(true)} className="btn-ghost text-xs" data-testid="btn-set-budget"><Coins size={14} weight="fill"/> Set budget</button>
          </div>
          {(report?.budget_lines || []).length === 0 ? (
            <div className="text-white/40 text-xs">No categories yet. Set a budget or log an expense to begin itemised tracking.</div>
          ) : (
            <div className="space-y-3">
              {report.budget_lines.map((l) => {
                const pct = l.pct == null ? (l.spent > 0 ? 100 : 0) : Math.min(100, l.pct);
                const color = l.over_budget ? "#E63946" : l.pct != null && l.pct > 85 ? "#FFB020" : "#00A86B";
                return (
                  <div key={l.category} data-testid={`budget-line-${l.category}`}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-white/80">{l.category}{l.allocated === 0 ? <span className="text-white/30 text-xs ml-2">unbudgeted</span> : null}</span>
                      <span className="mono text-xs" style={{ color: l.over_budget ? "#E63946" : "#9BA3A0" }}>
                        {cs}{Number(l.spent).toLocaleString()}{l.allocated > 0 ? ` / ${cs}${Number(l.allocated).toLocaleString()}` : ""}{l.pct != null ? ` · ${l.pct}%` : ""}
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
                    </div>
                    {l.over_budget && <div className="text-[10px] text-[#E63946] mt-1">Over budget by {cs}{Number(l.spent - l.allocated).toLocaleString()}</div>}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="rounded-xl bg-black/20 border border-white/5 p-5 mb-6">
          <div className="eyebrow mb-3">Log expense</div>
          <form onSubmit={addExp} className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
            <div className="md:col-span-2">
              <select value={form.receipt_id} onChange={e => linkReceipt(e.target.value)} data-testid="select-link-receipt">
                <option value="">Link receipt (optional)…</option>
                {receipts.map(r => <option key={r.receipt_id} value={r.receipt_id}>{r.vendor} · €{Number(r.amount).toFixed(2)} · {(r.date||"").slice(0,10)}</option>)}
              </select>
            </div>
            <input list="grant-cat-list" placeholder="Category" value={form.category} onChange={e => setForm({...form, category: e.target.value})} data-testid="input-exp-category"/>
            <datalist id="grant-cat-list">{CATS.map(c => <option key={c} value={c}/>)}</datalist>
            <input required type="number" step="0.01" placeholder="Amount" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} data-testid="input-exp-amount"/>
            <input placeholder="Description" value={form.description} onChange={e => setForm({...form, description: e.target.value})} data-testid="input-exp-desc"/>
            <button type="submit" data-testid="btn-add-expense" className="btn-gold text-sm"><Plus weight="bold" size={14}/> Log</button>
          </form>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
            <input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} data-testid="input-exp-date"/>
            <select value={form.currency} onChange={e => setForm({...form, currency: e.target.value})} data-testid="select-exp-currency">
              {["EUR","GBP","USD"].map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <input placeholder="Invoice / receipt reference" value={form.reference} onChange={e => setForm({...form, reference: e.target.value})} data-testid="input-exp-reference"/>
          </div>
          {form.currency !== grant.currency && <div className="text-[11px] text-white/45 mt-2">Converted to {grant.currency} at the ECB rate on the expense date; this grant is tracked in {grant.currency}.</div>}
        </div>

        <div className="max-h-72 overflow-auto">
          <table className="data">
            <thead><tr><th>Date</th><th>Category</th><th>Description</th><th>Reference</th><th className="text-right">Amount</th></tr></thead>
            <tbody>
              {expenses.length === 0 ? (
                <tr><td colSpan="5" className="text-center text-white/40 py-6">No expenses logged.</td></tr>
              ) : expenses.map(e => (
                <tr key={e.expense_id}>
                  <td className="mono text-xs">{e.date}</td>
                  <td>{e.category}</td>
                  <td className="text-white/70">{e.description}</td>
                  <td className="text-white/50 text-xs">{e.reference || (e.receipt_id ? <span className="text-[#00A86B]">● linked</span> : <span className="text-[#FFB020]">no ref</span>)}</td>
                  <td className="num">{cs}{Number(e.amount).toFixed(2)}{e.currency && e.currency !== grant.currency ? <div className="text-[10px] text-white/40">{Number(e.original_amount).toFixed(2)} {e.currency}</div> : null}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>
      {budgetOpen && <BudgetEditor grant={grant} lines={report?.budget_lines || []} onClose={() => setBudgetOpen(false)} onSaved={() => { setBudgetOpen(false); load(); }} />}
    </motion.div>
  );
}

function MilestoneChecklist({ m, onChange }) {
  const cl = m.checklist || [];
  const [text, setText] = useState("");
  const toggle = (i) => onChange(cl.map((c, idx) => idx === i ? { ...c, done: !c.done } : c));
  const remove = (i) => onChange(cl.filter((_, idx) => idx !== i));
  const add = (e) => { e.preventDefault(); const t = text.trim(); if (!t) return; onChange([...cl, { label: t, done: false }]); setText(""); };
  return (
    <div className="mt-2 pl-8" data-testid={`milestone-checklist-${m.milestone_id}`}>
      {cl.map((c, i) => (
        <div key={i} className="flex items-center gap-2 py-0.5">
          <button type="button" onClick={() => toggle(i)} data-testid={`checklist-toggle-${m.milestone_id}-${i}`}>
            {c.done ? <CheckSquare size={15} weight="fill" color="#00A86B" /> : <Square size={15} color="#8A9A94" />}
          </button>
          <span className={`text-[12px] ${c.done ? "text-white/40 line-through" : "text-white/70"}`}>{c.label}</span>
          <button type="button" onClick={() => remove(i)} className="text-white/30 hover:text-[#E63946] ml-auto" data-testid={`checklist-del-${m.milestone_id}-${i}`}><X size={11}/></button>
        </div>
      ))}
      <form onSubmit={add} className="flex items-center gap-2 mt-1">
        <input list="evidence-suggestions" value={text} onChange={(e) => setText(e.target.value)} placeholder="Add evidence to submit…" className="!text-xs !py-1" data-testid={`checklist-input-${m.milestone_id}`} />
        <button type="submit" className="btn-ghost !text-[11px] !py-1 whitespace-nowrap" data-testid={`checklist-add-${m.milestone_id}`}><Plus size={12}/> Add</button>
      </form>
    </div>
  );
}


const KpiTile = ({ label, value, hint, color, warn }) => (  <div className={`glass rounded-xl p-4 ${warn ? "ring-1 ring-[#E63946]/40" : ""}`}>
    <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">{label}</div>
    <div className="mono text-xl mt-2" style={color ? { color } : undefined}>{value}</div>
    {hint ? <div className="text-[11px] text-white/45 mt-1">{hint}</div> : null}
  </div>
);

function BudgetEditor({ grant, lines, onClose, onSaved }) {
  const cs = sym(grant.currency);
  const [rows, setRows] = useState(() => {
    const existing = lines.filter(l => l.allocated > 0).map(l => ({ category: l.category, allocated: String(l.allocated) }));
    return existing.length ? existing : [{ category: "Equipment", allocated: "" }];
  });
  const set = (i, k, v) => setRows(rs => rs.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const addRow = () => setRows(rs => [...rs, { category: "", allocated: "" }]);
  const rm = (i) => setRows(rs => rs.filter((_, idx) => idx !== i));
  const total = rows.reduce((s, r) => s + (Number(r.allocated) || 0), 0);
  const over = total > grant.total_awarded;

  const save = async () => {
    const budget_lines = rows.filter(r => r.category.trim()).map(r => ({ category: r.category.trim(), allocated: Number(r.allocated) || 0 }));
    await api.put(`/grants/${grant.grant_id}/budget`, { budget_lines });
    onSaved();
  };

  return (
    <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[60] flex items-start justify-center p-4 overflow-y-auto" data-testid="budget-editor">
      <motion.div initial={{y:20,opacity:0}} animate={{y:0,opacity:1}} className="glass rounded-2xl w-full max-w-lg my-8 p-8">
        <div className="flex items-center justify-between mb-6">
          <div><div className="eyebrow mb-1">Itemised budget</div><div className="font-display text-2xl">Allocate {grant.name}</div></div>
          <button onClick={onClose} className="text-white/60 hover:text-white" data-testid="btn-close-budget"><X size={20}/></button>
        </div>
        <div className="space-y-3">
          {rows.map((r, i) => (
            <div key={i} className="flex gap-3 items-center">
              <input list="grant-cat-list" placeholder="Category" value={r.category} onChange={e => set(i, "category", e.target.value)} data-testid={`budget-cat-${i}`} className="flex-1"/>
              <input type="number" step="0.01" placeholder={`Allocated ${cs}`} value={r.allocated} onChange={e => set(i, "allocated", e.target.value)} data-testid={`budget-amt-${i}`} className="w-36"/>
              <button onClick={() => rm(i)} className="text-white/40 hover:text-[#E63946]"><Trash size={16}/></button>
            </div>
          ))}
          <datalist id="grant-cat-list">{CATS.map(c => <option key={c} value={c}/>)}</datalist>
          <button onClick={addRow} className="btn-ghost text-xs" data-testid="btn-add-budget-row"><Plus size={14}/> Add category</button>
        </div>
        <div className={`text-sm mt-4 ${over ? "text-[#E63946]" : "text-white/60"}`}>
          Allocated {cs}{total.toLocaleString()} of {cs}{Number(grant.total_awarded).toLocaleString()} {over ? "— exceeds award!" : `· ${cs}${(grant.total_awarded - total).toLocaleString()} unallocated`}
        </div>
        <div className="flex justify-end gap-3 pt-4 mt-4 border-t border-white/5">
          <button onClick={onClose} className="btn-ghost">Cancel</button>
          <button onClick={save} className="btn-gold" data-testid="btn-save-budget">Save budget</button>
        </div>
      </motion.div>
    </motion.div>
  );
}
